<template>
  <div>
    <div
      ref="filterOptionSection"
      class="filter-option-section"
    >
      <div
        class="search-option-icon-container"
        @click="updateSearchItems"
      >
        <i :class="searchArrow" />
      </div>
      <div style="width:100%">
        <Input
          v-model="searchText"
          search
          enter-button
          clearable
          size="small"
          placeholder="Search"
          class="filter-row search-input"
          @on-search="updateFilterResults"
          @on-clear="resetSearch"
        />
        <div
          v-show="searchExpanded"
          class="hidden-section"
        >
          <Select
            v-show="searchOptions.length > 1"
            v-model="searchOption"
            size="small"
            class="filter-row"
          >
            <Option
              v-for="option in searchOptions"
              :key="option.value"
              :label="option.label"
              :value="option.value"
            />
          </Select>

          <Select
            v-model="searchType"
            size="small"
            class="filter-row"
          >
            <Option
              v-for="option in searchTypeOptions"
              :key="option.value"
              :label="option.label"
              :value="option.value"
            />
          </Select>
        </div>
        <p
          v-if="totalResults > 0"
          class="total-results"
        >
          {{ totalResultsLabel }}
        </p>
        <p
          v-else-if="showResultError"
          class="error"
        >
          No results found. Review your settings.
        </p>
      </div>
    </div>

    <Scroll
      v-if="totalResults > 0"
      :height="resultPanelHeight"
      :distance-to-edge="scrollEdgeDistances"
      class="result-tree-scroll"
    >
      <SlVueTree
        v-model="filteredResults"
        :allow-multiselect="false"
        @nodedblclick="doubleClickNode"
      >
        <template
          slot="toggle"
          slot-scope="{ node }"
        >
          <span
            v-if="node.children.length"
            class="item-icon toggle"
          >
            <i
              v-if="!node.isExpanded"
              class="fas fa-caret-right"
            />
            <i
              v-else-if="node.isExpanded"
              class="fas fa-caret-down"
            />
          </span>
        </template>
        <template
          slot="title"
          slot-scope="{ node }"
        >
          <span :class="{ 'item-icon': true, 'file': node.isLeaf }">
            <i
              v-if="node.data.isRepo && node.data.isRoot"
              class="fab fa-google-drive"
            />
            <i
              v-else-if="node.data.linkedToRepo"
              class="fas fa-link"
            />
            <i
              v-else-if="node.isLeaf"
              class="far fa-file"
            />
            <i
              v-else-if="!node.isLeaf && (!node.isExpanded || !node.children.length)"
              class="fas fa-folder"
            />
            <i
              v-else-if="!node.isLeaf && node.isExpanded && node.children.length"
              class="fas fa-folder-open"
            />
          </span>
          {{ node.title }}
        </template>
      </SlVueTree>
    </Scroll>
  </div>
</template>
<script>
import SlVueTree from 'sl-vue-tree'
import _ from 'lodash'

const ALL = 'all'
const LOCAL_WORKSPACE = 'local-workspace'
const SHARED_WORKSPACE = 'shared-workspace'

export default {
  components: {
    SlVueTree
  },

  props: {
    height: Number,
    workspaceTree: Object
  },

  data () {
    return {
      searchText: '',
      searchType: 'both',
      searchExpanded: false,
      searchOption: ALL,
      searchOptions: [
        {
          label: 'Search All',
          value: ALL
        }, {
          label: 'Search in Local Workspace',
          value: LOCAL_WORKSPACE
        }, {
          label: 'Search in Shared Workspace',
          value: SHARED_WORKSPACE
        }
      ],
      searchTypeOptions: [
        {
          label: 'Search only in Query',
          value: 'content'
        },
        {
          label: 'Search only in File/Folder name',
          value: 'file'
        },
        {
          label: 'Both',
          value: 'both'
        }
      ],
      resultPanelHeight: this.panelHeight - 42,
      filteredResults: [],
      totalResults: 0,
      scrollEdgeDistances: [20, 20],
      workspaceTreeData: this.workspaceTree,
      resetFilterResults: true
    }
  },

  computed: {
    searchArrow () {
      const expandedClass = this.searchExpanded ? 'expanded' : ''
      return `fas fa-caret-right selector ${expandedClass}`
    },

    totalResultsLabel () {
      const resultLabel = this.totalResults === 1 ? 'result' : 'results'
      return `${this.totalResults} ${resultLabel}`
    },

    showResultError () {
      return !this.resetFilterResults && this.filteredResults.length < 0
    },

    panelHeight () {
      return this.height
    }
  },

  watch: {
    workspaceTree (newWorkspaceTree) {
      this.workspaceTreeData = newWorkspaceTree
    }
  },

  methods: {
    /**
     * Toggles the search items visibility and updates the search panel height.
     */
    updateSearchItems () {
      this.searchExpanded = !this.searchExpanded
      this.updateResultPanelHeight()
    },

    /**
     * Updates the search panel height according with the search section height.
     */
    updateResultPanelHeight () {
      this.$nextTick(() => {
        this.resultPanelHeight = this.panelHeight - this.$refs.filterOptionSection.clientHeight
      })
    },

    /**
     * Updates the filter results if there's any keyword.
     */
    updateFilterResults () {
      if (!this.searchText.length) {
        return
      }

      // Make a deep clone of the existing children in root in order to not modify the original array.
      this.totalResults = 0
      const sourceCopy = _.cloneDeep(this.workspaceTreeData)
      this.filteredResults = this.filterWorkspaceResults(sourceCopy.children)
      this.resetFilterResults = false
      this.updateResultPanelHeight()
    },

    /**
     * Filters the workspace recursively to fetch all files that matches with the provided keywords.
     * It can also fetch folders that matches with the provided keywords if the "Include Folders" checkbox
     * is active.
     * @param {Array} workspaceChildren
     * @return {Array}
     */
    filterWorkspaceResults (workspaceChildren) {
      const selectedItems = []
      workspaceChildren.forEach(workspaceItem => {
        const itemMatchesFilter = ['both', 'file'].includes(this.searchType) && workspaceItem.title.toLowerCase().includes(this.searchText.toLowerCase())
        const contentMatchesFilter = ['both', 'content'].includes(this.searchType) && workspaceItem.isLeaf && workspaceItem.query.queryText.toLowerCase().includes(this.searchText.toLowerCase())
        const correctWorkspace = (
          this.searchOption === ALL ||
          (workspaceItem.isRepo && this.searchOption === SHARED_WORKSPACE) ||
          (!workspaceItem.isRepo && this.searchOption === LOCAL_WORKSPACE)
        )

        if (!workspaceItem.isLeaf) {
          const itemChildren = workspaceItem.children || []
          const filteredFolder = this.filterWorkspaceResults(itemChildren)

          if ((correctWorkspace && itemMatchesFilter) || filteredFolder.length) {
            workspaceItem.isExpanded = true
            workspaceItem.children = filteredFolder
            selectedItems.push(workspaceItem)
            this.totalResults += itemMatchesFilter ? 1 : 0
          }
        } else if (correctWorkspace && (itemMatchesFilter || contentMatchesFilter)) {
          workspaceItem.isDraggable = false
          selectedItems.push(workspaceItem)
          this.totalResults++
        }
      })

      return selectedItems
    },

    /**
     * Resets and hides the search results
     */
    resetSearch () {
      this.totalResults = 0
      this.filteredResults = []
      this.updateResultPanelHeight()
    },

    /**
     * Emitted when a node on the search tree is double clicked
     */
    doubleClickNode (fileNode) {
      this.$emit('node-dbl-click', fileNode)
    }
  }
}
</script>
<style scoped>
  .filter-option-section {
    margin: 8px 4px 0;
    display: flex;
  }

  .search-option-icon-container {
    align-items: center;
    margin-top: 4px;
    margin-right: 4px;
  }

  .filter-row {
    margin-bottom: 4px;
  }

  .search-input >>> .ivu-icon-ios-close-circle {
    right: 40px;
  }

  .fa-caret-right {
    cursor: pointer;
  }

  .fa-caret-right.selector.expanded {
    transform: rotate(45deg);
  }

  .total-results {
    font-style: italic;
    color: var(--color-gray);
  }

  .result-tree-scroll >>> .ivu-scroll-content {
    margin-top: -8px;
  }
</style>
